import { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { jsx } from 'react/jsx-runtime';
import { of, Subject, pipe, BehaviorSubject, combineLatest } from 'rxjs';
import { startWith, takeUntil, filter, map, tap, toArray, first, concatMap, withLatestFrom, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { immerable, produce } from 'immer';
import { Store, Query, applyTransaction } from '@datorama/akita';

var Type = Function;
function isType(v) {
  return typeof v === 'function';
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

/**
 * The 1st injector instance is considered the 'root' injector
 * The root injector my be auto-added as a parent to all subsequent injector instances
 */
var rootInjector;
/**
 * Utility function used to easily create 1..n injectors; each with thier
 * own singletons and provider registry.
 *
 * NOTE: If only a class is registered (instead of a Provider), convert to it
 * for normalized usages
 */

function makeInjector(registry, parent) {
  var isParentDefined = typeof parent !== 'undefined'; // Always use the Provider long-form to register, e.g
  // { provide: MyClazz, useClass: MyClazz } instead of MyClazz

  var normalized = registry.map(function (it) {
    var isProvider = !!it.provide;
    return isProvider ? it : makeClassProvider(it);
  }); // Auto add root injector as parent unless the parent is already defined...

  var instance = new Injector(normalized, isParentDefined ? parent : rootInjector);
  !rootInjector && (rootInjector = instance);
  return instance;
}
/**
 * Injector class that manages a registry of Providers and a registry
 * of singleton instances [singletons for the instance of the injector]
 */

var Injector = /*#__PURE__*/function () {
  function Injector() {
    var providers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var parent = arguments.length > 1 ? arguments[1] : undefined;

    _classCallCheck(this, Injector);

    this.providers = providers;
    this.parent = parent;
    this.singletons = new WeakMap();
    this.addProviders(providers);
  }
  /**
   * Lookup singleton instance using token
   * Optionally create instance and save as singleton if needed
   * If not found, this will search a parent injector (if provided)
   */


  _createClass(Injector, [{
    key: "get",
    value: function get(token) {
      var inst = this.findAndMakeInstance(token);
      return inst || (this.parent ? this.parent.get(token) : null);
    }
    /**
     * Force-clear internally singleton cache.
     * If parents are specified, this enforces correct parent lookups
     * for future instantiations.
     */

  }, {
    key: "reset",
    value: function reset() {
      if (this.parent) {
        this.singletons = new WeakMap();
      }
    }
    /**
     * Create an unshared, non-cached instance of the token;
     * based on the Provider configuration
     * If not found, then consider asking parent injector for the
     * instance
     */

  }, {
    key: "instanceOf",
    value: function instanceOf(token) {
      var inst = this.instanceFromRegistry(token) || this.instanceFromParents(token);

      if (!inst) {
        throw new Error("Unable make instance of ".concat(String(token)));
      }

      return inst;
    }
    /**
     * Dynamically allow Provider registrations and singleton overwrites
     * Provide an 'restore' function to optionally restore original providers (if replaced),
     *
     * @param registry Configuration set of Provider(s)
     * @param replace Replace existing provider
     */

  }, {
    key: "addProviders",
    value: function addProviders(registry) {
      var _this = this;

      var origProviders = _toConsumableArray(this.providers);

      this.providers = mergeProviders(this.providers, registry);
      registry.map(function (it) {
        return _this.singletons["delete"](it.provide);
      });
      return function () {
        return _this.addProviders(origProviders);
      };
    }
    /**
     * Get all Token providers from parent(s), updated with current overwrites (if any).
     *
     * This allows the full parent registry tree to be flattened
     * with current level acting as overwrites
     */

  }, {
    key: "getFlatProviderTree",
    value: function getFlatProviderTree() {
      return !this.parent ? _toConsumableArray(this.providers) : _toConsumableArray(mergeProviders(this.parent.getFlatProviderTree(), this.providers));
    } // *************************************************
    // Private  Methods
    // *************************************************

    /**
     * Ask parent injectors (if any) for a flattened Provider registry,
     * then try to build the instance. When done, restore the original
     * Provider registry for this injector
     */

  }, {
    key: "instanceFromParents",
    value: function instanceFromParents(token) {
      if (!this.parent) return undefined;

      var original = _toConsumableArray(this.providers);

      try {
        this.providers = mergeProviders(this.getFlatProviderTree(), this.providers);
        return this.instanceFromRegistry(token);
      } finally {
        this.providers = original;
      }
    }
    /**
     * Find last Provider registration (last one wins)
     */

  }, {
    key: "findLastRegistration",
    value: function findLastRegistration(token, list) {
      var registry = this.providers.filter(function (it) {
        return it.provide === token;
      });
      return registry.length ? registry[registry.length - 1] : null;
    }
    /**
     * Based on provider registration, create instance of token and save
     * as singleton value.
     * NOTE: do not scan parent since we are caching singletons at this level only.
     *
     * @param token Class, value, or factory
     */

  }, {
    key: "findAndMakeInstance",
    value: function findAndMakeInstance(token) {
      var isString = typeof token === 'string';
      var isNumber = !isString && typeof token === 'number';

      if (isString || isNumber) {
        return token;
      }

      var result = this.singletons.get(token);

      if (!result) {
        result = this.instanceOf(token);
        this.singletons.set(token, result);
      }

      return result;
    }
  }, {
    key: "instanceFromRegistry",
    value: function instanceFromRegistry(token) {
      var makeAndCache = this.findAndMakeInstance.bind(this);
      var provider = this.findLastRegistration(token, this.providers);
      var deps = provider && provider.deps ? provider.deps.map(makeAndCache) : []; // const makeWithClazz = (clazz: any) => (clazz ? new clazz(...deps) : null);

      var makeWithClazz = function makeWithClazz(clazz) {
        return clazz ? new (clazz.bind.apply(clazz, __doSpread.call(null, [void 0], deps)))() : null;
      }; // eslint-disable-line prefer-spread


      var makeWithFactory = function makeWithFactory(fn) {
        return fn ? fn.apply(null, deps) : null;
      }; // eslint-disable-line prefer-spread


      return provider && (provider.useValue || makeWithClazz(provider.useClass) || makeWithFactory(provider.useFactory) || makeWithClazz(provider.provide)) // fallback uses the token as a `class`
      ;
    }
  }]);

  return Injector;
}();
/**
 * Internal utility used to normalized Provider entries
 * during a `makeInjector()` call
 *
 * @param token
 */


function makeClassProvider(token) {
  return {
    provide: token,
    useClass: token,
    deps: _toConsumableArray(token['deps'] || [])
  };
}
/**
 * Merge current provider registry with 1..n updated Provider settings;
 * where the `updated` set may contain either modified and/or new configurations
 */


function mergeProviders(current, updated) {
  var extractToken = function extractToken(it) {
    return it.provide;
  };

  var findByToken = function findByToken(token) {
    return allEntries.find(function (it) {
      return it.provide === token;
    });
  };

  var allEntries = [].concat(_toConsumableArray(updated), _toConsumableArray(current));
  var allTokens = new Set(allEntries.map(extractToken)); // Reduce combined set to entries unique by Token, prioritize updated entries first.

  return Array.from(allTokens).map(findByToken);
} // ***************************************************************************************************
//  Due to issues with compiling ESM with `new clazz(...deps)`, we will use a special spread function
// ***************************************************************************************************


function __doSpread() {
  for (var _len = arguments.length, params = new Array(_len), _key = 0; _key < _len; _key++) {
    params[_key] = arguments[_key];
  }

  for (var s = 0, i = 0, il = params.length; i < il; i++) {
    s += params[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = params[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
}

/**
 * React Context to 'provide' the injector
 * Used with code like:
 *
 * ```ts
 * 	export const DependencyInjectionProvider: React.FC = ({ injector, children }) => {
 *		return (
 * 				<InjectorContext.Provider value={injector}>
 *				  {children}
 *				</InjectorContext.Provider>
 *		);
 *  };
 * ```
 */

var InjectorContext = /*#__PURE__*/createContext(null);

/**
 * @description
 *
 * Creates a token that can be used in a DI Provider.
 *
 * Use an `InjectionToken` whenever the type you are injecting is not reified (does not have a
 * runtime representation) such as when injecting an interface, callable type, array or
 * parameterized type.
 *
 * `InjectionToken` is parameterized on `T` which is the type of object which will be returned by
 * the `Injector`. This provides additional level of type safety.
 *
 * ```
 * interface MyInterface {...}
 * var myInterface = injector.get(new InjectionToken<MyInterface>('SomeToken'));
 * // myInterface is inferred to be MyInterface.
 * ```
 *
 * When creating an `InjectionToken`, you can optionally specify a factory function which returns
 * (possibly by creating) a default value of the parameterized type `T`. This sets up the
 * `InjectionToken` using this factory as a provider as if it was defined explicitly in the
 * application's root injector. If the factory function, which takes zero arguments, needs to inject
 * dependencies, it can do so using the `inject` function. See below for an example.
 *
 * Additionally, if a `factory` is specified you can also specify the `providedIn` option, which
 * overrides the above behavior and marks the token as belonging to a particular `@NgModule`. As
 * mentioned above, `'root'` is the default value for `providedIn`.
 *
 * @publicApi
 */
var InjectionToken = /*#__PURE__*/function () {
  function InjectionToken(_desc) {
    _classCallCheck(this, InjectionToken);

    this._desc = _desc;
  }

  _createClass(InjectionToken, [{
    key: "toString",
    value: function toString() {
      return "InjectionToken ".concat(this._desc);
    }
  }]);

  return InjectionToken;
}();

function useInjectorHook(token, injector) {
  return [injector.get(token), injector];
}

/**
 * Return either the injector instance or the token lookup FROM the
 * injector.
 *
 * A configured injector instance is required along with and a lookup token.
 * What is returned is a tuple containing the singleton instance and the injector.
 *
 * @code
 *   const injector: DependencyInjector = makeInjector([
 *     { provide: API_KEY, useValue: '873771d7760b846d51d5ac5804ab' },
 *     { provide: API_ENDPOINT, useValue: 'https://uifaces.co/api?limit=25' },
 *     { provide: ContactsService, useClass: ContactsService, deps: [API_ENDPOINT, API_KEY] }
 *   ]);
 *
 *   // Hide how DI is used under the hood
 *   // NOTE: The requires a DependencyInjectionProvider to be used for DI lookups
 *
 *   export function useContactsHook(): ContactsService {
 *     return useDependencyInjector<ContactsService>(ContactsService)
 *   }
 *
 * @returns T is either the DependencyInjector or the injector token lookup.
 */

var useDependencyInjector = function useDependencyInjector(token) {
  var injector = useContext(InjectorContext);
  return !token ? injector : injector.get(token);
};

/**
 * Dependency Injection is the fundamental mechanism for management of non-UI
 * entities (services, constants, facades, etc). Great DI provides:
 *
 * - Configure of dependencies between entities
 * - On-Demand construction/instantiation of entities
 * - Caching
 * - Singleton or Factory Access
 * - Easy lookups from the View Hierarchy
 * - Easy lookups for the Business Layers
 *
 */

var DependencyInjectionProvider = function DependencyInjectionProvider(_ref) {
  var injector = _ref.injector,
      children = _ref.children;
  return jsx(InjectorContext.Provider, Object.assign({
    value: injector
  }, {
    children: children
  }), void 0);
};

var DestroyEvent = '[EventBus] destory';
var destroyEventBus = function destroyEventBus() {
  return {
    type: DestroyEvent
  };
};
/**
 * Simply Pub/Sub mechanism that support decoupled communication between services
 * Note: This EventBus does cache the most recent event for EACH type...
 */

var EventBus = /*#__PURE__*/function () {
  function EventBus() {
    var enableLogs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    _classCallCheck(this, EventBus);

    this.enableLogs = enableLogs;
    this.cache = {};
    this.destroy$ = of({
      type: 'waiting'
    });
    this.emitter = new Subject();
    this.listenForDestroy();
    this.captureEvents();
  }
  /**
   * Public API to stop all current subscriptions
   * and reset with clean EventBus
   */


  _createClass(EventBus, [{
    key: "reset",
    value: function reset() {
      this.emit(destroyEventBus());
    }
    /**
     * Emit an event to all listeners on this messaging queu
     */

  }, {
    key: "emit",
    value: function emit(event) {
      this.enableLogs && console.log("[EventBus] emit(".concat(event.type, ")"));
      this.emitter.next(event);
    }
    /**
     * Easily listen to a collection of events
     * And provide single teardown to disconnect all
     * internal connections.
     */

  }, {
    key: "onMany",
    value: function onMany(collection) {
      var _this = this;

      var eventKeys = Object.keys(collection);
      var connections = eventKeys.map(function (key) {
        return _this.on(key, collection[key]);
      });
      return function () {
        connections.map(function (teardown) {
          return teardown();
        });
      };
    }
    /**
     * Listen on a single event, extract data
     * Publish a teardown function to disconnect later
     */

  }, {
    key: "on",
    value: function on(event, notify) {
      var _this2 = this;

      var watch$ = this.emitter.pipe(startWith(this.cache[event]), takeUntil(this.destroy$), filter(function (e) {
        return (e === null || e === void 0 ? void 0 : e.type) === event;
      }), map(function (e) {
        return e.data;
      }));
      var subscription = watch$.subscribe(function (data) {
        _this2.enableLogs && console.log("[EventBus] on(".concat(event, ")"));
        notify(data);
      });
      return subscription.unsubscribe.bind(subscription);
    }
    /**
     * Get an observable stream for a specific event
     */

  }, {
    key: "observableFor",
    value: function observableFor(event) {
      var watch$ = this.emitter.pipe(takeUntil(this.destroy$), filter(function (e) {
        return e.type === event;
      }), map(function (e) {
        return e.data;
      }));
      return watch$;
    }
    /**
     * Enable events to stop ALL subscriptions
     * Create special stream that ONLY emits destroy events
     */

  }, {
    key: "listenForDestroy",
    value: function listenForDestroy() {
      var _this3 = this;

      var clearCache = function clearCache() {
        return _this3.cache = {};
      };

      var onlyDestroyEvents = function onlyDestroyEvents(_ref) {
        var type = _ref.type;
        return type === DestroyEvent;
      };

      this.destroy$ = this.emitter.pipe(filter(onlyDestroyEvents), tap(clearCache));
    }
    /**
     * Activate event interceptor to record last emission for each event type
     * NOTE: do not capture the 'destroy' event
     */

  }, {
    key: "captureEvents",
    value: function captureEvents() {
      var _this4 = this;

      var captureToCache = function captureToCache(e) {
        return _this4.cache[e.type] = e;
      };

      this.emitter.pipe(takeUntil(this.destroy$), tap(captureToCache)).subscribe();
    }
  }]);

  return EventBus;
}();

/**
 * Special hook to provide access to the 'nearest' EventBus instance.
 * From a custom hooke, developers can now easily access the
 *
 *  `notify(<event>)` and
 *  `listen(<event type>, handler)`
 *
 * features of the EventBus. This hook also manages component listeners
 * and !!auto-disconnects during dismounts.
 *
 * NOTE: this hook should ONLY be used under special circumstances. EventBus
 * messaging is NOT intended for generalized messaging.
 *
 * @returns [notifyFn, listenerFn]
 */

var useEventBus = function useEventBus(context) {
  var injector = useContext(context || InjectorContext);

  var _useState = useState(function () {
    return injector.get(EventBus);
  }),
      _useState2 = _slicedToArray(_useState, 1),
      eventBus = _useState2[0];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      registry = _useState4[0],
      setRegistry = _useState4[1];

  var notify = useCallback(function (e) {
    eventBus.emit(e);
  }, []);
  var listen = useCallback(
  /**
   * Cache the unsubscribe AND trigger UI updates
   * since the listener is notified immediately and may
   * require UI re-renders.
   */
  function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    var unsubscribe = eventBus.on.apply(eventBus, args);
    setRegistry(function (l) {
      return [].concat(_toConsumableArray(l), [unsubscribe]);
    });
  }, []);
  useEffect(function () {
    return function () {
      registry.map(function (unsubscribe) {
        return unsubscribe();
      });
    };
  }, []);
  return [notify, listen];
};

/**
 * These event utils are purposed to announce entity collection changes.
 *
 * This also demonstrates how to build EventBus messaging using the `createEvent()` pattern.
 * eg   `bus.emit( itemUpdated(item) )`
 */
var CollectionEvent;

(function (CollectionEvent) {
  CollectionEvent["ITEM_ADDED"] = "itemAdded";
  CollectionEvent["ITEM_UPDATED"] = "itemUpdated";
  CollectionEvent["ITEM_REMOVED"] = "itemRemoved";
  CollectionEvent["ITEM_ERROR"] = "eventError";
})(CollectionEvent || (CollectionEvent = {}));

var ItemEvent = function ItemEvent(type, data) {
  _classCallCheck(this, ItemEvent);

  this.type = type;
  this.data = data;
};
var itemUpdated = function itemUpdated(item) {
  return new ItemEvent(CollectionEvent.ITEM_UPDATED, item);
};
var itemRemoved = function itemRemoved(itemId) {
  return new ItemEvent(CollectionEvent.ITEM_REMOVED, itemId);
};
var itemError = function itemError(error) {
  return new ItemEvent(CollectionEvent.ITEM_ERROR, error);
};

/**
 * @whatItDoes reads all the values from an observable and returns a promise
 * with an array of all values. This should be used in combination with async/await.
 * NOTE:  !!This requires the stream to 'complete'
 *
 * ## Example
 *
 * ```typescript
 * const obs = of(1, 2, 3, 4);
 * const res = await readAll(obs)
 * expect(res).toEqual([1, 2, 3, 4]);
 * ```
 */

function readAll(o) {
  return o.pipe(toArray()).toPromise();
}
/**
 * @whatItDoes reads the first value from an observable and returns a promise
 * with it. This should be used in combination with async/await.
 *
 * ## Example
 *
 * ```typescript
 * const obs = of(1, 2, 3, 4);
 * const res = await readFirst(obs)
 * expect(res).toEqual(1);
 * ```
 */

function readFirst(o) {
  return o.pipe(first()).toPromise();
}

/**
 * 'concatLatestFrom' combines the source value
 * and the last available value from a lazily evaluated Observable
 * in a new array
 */

function concatLatestFrom(observablesFactory) {
  return pipe(concatMap(function (value) {
    var observables = observablesFactory(value);
    var observablesAsArray = Array.isArray(observables) ? observables : [observables];
    return of(value).pipe(withLatestFrom.apply(void 0, _toConsumableArray(observablesAsArray)));
  }));
}

/**
 * Manage Observable subscµription and report value emission.
 * - support dynamic reset of stream source
 * - support auto-unsubscribe when view component unmounts
 * @param observable$ Stream source
 * @param initialValue initial value; to simulate 1st stream emission
 */

function useObservable(observable$, initialValue) {
  var _useState = useState(observable$),
      _useState2 = _slicedToArray(_useState, 2),
      source$ = _useState2[0],
      setObservable = _useState2[1];

  var _useState3 = useState(initialValue),
      _useState4 = _slicedToArray(_useState3, 2),
      value = _useState4[0],
      setValue = _useState4[1];

  var reportError = function reportError(err) {
    setValue(undefined);
    console.error("useObservable() error: ".concat(JSON.stringify(err)));
  };

  useEffect(function () {
    if (source$) {
      var s = source$.subscribe(setValue, reportError);
      return function () {
        s.unsubscribe();
      };
    }
  }, [source$]);
  return [value, setObservable];
}

var _a;
var PAGE_SIZE = 20;
/**
 * Easily add in-memory Pagination features to a dataset.
 * This wrapper will internally manage pagination settings
 * and publish result page-set of data.
 *
 * @Sample:
 *
 *  // Do not use inside state management/reducers where
 *  // only 'objects' are expected during mutation
 *
 *  const paginator = new DataPaginator<PersonSummary>();
 *
 *  // Later when processing actions:
 *
 *  paginator.goToPage((action as NavigateToPage).page);
 *  const { totalPages, currentPage, paginatedList, pageSize } = paginator;
 *
 *  state = {
 *    ...state,
 *    paginator: {
 *     paginatedList,
 *      currentPage,
 *      totalPages,
 *      pageSize
 *    }
 *  };
 */

var DataPaginator = /*#__PURE__*/function () {
  /**
   * Construct with rawList and options
   */
  function DataPaginator() {
    var _rawList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

    var _pageSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PAGE_SIZE;

    var _currentPage = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;

    _classCallCheck(this, DataPaginator);

    this._rawList = _rawList;
    this._pageSize = _pageSize;
    this._currentPage = _currentPage;
    this[_a] = true;
    this._totalPages = 0;
    this._pageSize = Math.max(1, Math.round(this._pageSize));
    this.goToPage(_currentPage);
  }

  _createClass(DataPaginator, [{
    key: "totalCount",
    get: function get() {
      return this._rawList.length;
    }
    /**
     * Read-only accessor
     */

  }, {
    key: "totalPages",
    get: function get() {
      return this._totalPages;
    }
    /**
     * Read-only accessor to 1-based page marker
     */

  }, {
    key: "currentPage",
    get: function get() {
      return this._currentPage + 1;
    }
  }, {
    key: "paginatedList",
    get: function get() {
      return this._paginatedList ? _toConsumableArray(this._paginatedList) : [];
    }
    /**
     * Always return immutable list
     */

  }, {
    key: "rawList",
    get: function get() {
      return _toConsumableArray(this._rawList);
    },
    set: function set(list) {
      if (list !== this._rawList) {
        this._rawList = _toConsumableArray(list || []);
        this._paginatedList = [];
        this._currentPage = -1;
        this.goToPage(1);
      }
    }
  }, {
    key: "pageSize",
    get: function get() {
      return this._pageSize;
    },
    set: function set(val) {
      if (val !== this._pageSize) {
        this._currentPage = 1;
        this._paginatedList = [];
        this._pageSize = Math.max(0, Math.round(val));
        this.goToPage(1);
      }
    }
    /**
     * External users use 1-based values,
     * internal usages maintain 0-based values
     */

  }, {
    key: "goToPage",
    value: function goToPage(page) {
      this.updateTotalPages();
      page = Math.min(Math.max(1, page), this._totalPages);

      if (this._totalPages > 0) {
        // Use 0-based page value and force to be in-bounds
        page = Math.min(Math.max(0, Math.floor(page) - 1), this._totalPages - 1);

        if (page > -1) {
          if (page !== this._currentPage || !this._paginatedList) {
            var start = page * this._pageSize;
            var end = start + this._pageSize;

            if (start <= this.rawList.length - 1) {
              this._paginatedList = this.rawList.slice(start, end);
            }

            this._currentPage = page;
          }
        }
      }

      return this._paginatedList;
    }
    /**
     * Needed for the 'Paginator' API
     */

  }, {
    key: "setPageSize",
    value: function setPageSize(numRowsPerPage) {
      this.pageSize = numRowsPerPage || PAGE_SIZE;
    }
    /**
     * Republish updated total num pages
     */

  }, {
    key: "updateTotalPages",
    value: function updateTotalPages() {
      this._totalPages = Math.ceil(this._rawList.length / this._pageSize);

      if (!this._totalPages) {
        this._currentPage = -1;
      }
    }
  }]);

  return DataPaginator;
}();
_a = immerable;

var useStorePaginator = function useStorePaginator(rawList) {
  var pageSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 10;

  var _useState = useState(function () {
    return new DataPaginator(rawList, pageSize);
  }),
      _useState2 = _slicedToArray(_useState, 1),
      paginator = _useState2[0];

  var setPageSize = useCallback(function (numRows) {
    paginator.pageSize = numRows;
    announceChanges();
  }, [paginator]);
  var goToPage = useCallback(function (page) {
    var results = paginator.goToPage(page);
    announceChanges();
    return results;
  }, [paginator]);
  var announceChanges = useCallback(function () {
    var totalCount = paginator.totalCount,
        totalPages = paginator.totalPages,
        currentPage = paginator.currentPage,
        paginatedList = paginator.paginatedList,
        pageSize = paginator.pageSize;
    setPaginationVM({
      totalCount: totalCount,
      totalPages: totalPages,
      currentPage: currentPage,
      paginatedList: paginatedList,
      pageSize: pageSize,
      goToPage: goToPage,
      setPageSize: setPageSize
    });
  }, [paginator, goToPage, setPageSize]);

  var _useState3 = useState(paginator),
      _useState4 = _slicedToArray(_useState3, 2),
      vm = _useState4[0],
      setPaginationVM = _useState4[1];

  useEffect(function () {
    paginator.rawList = rawList;
    announceChanges();
    return function () {
      paginator.rawList = [];
    };
  }, [paginator, rawList, announceChanges]);
  return vm;
};

/**
 * Reactive Paginator service built-in pagination features
 * and observable updates of pagination state
 */

var RxPaginator = /*#__PURE__*/function () {
  function RxPaginator() {
    var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var pageSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 10;

    _classCallCheck(this, RxPaginator);

    this.paginator = new DataPaginator(source, pageSize);
    this.emitter = new BehaviorSubject({});
    this.pagination$ = this.emitter.asObservable();
    this.announceChanges();
  }

  _createClass(RxPaginator, [{
    key: "goToPage",
    value: function goToPage(page) {
      var results = this.paginator.goToPage(page);
      this.announceChanges();
      return results;
    }
  }, {
    key: "setPageSize",
    value: function setPageSize(numRowsPerPage) {
      this.paginator.pageSize = numRowsPerPage;
      this.announceChanges();
    }
  }, {
    key: "dataSource",
    set: function set(source) {
      var current = this.toSnapshot();
      this.paginator.rawList = source;
      this.paginator.goToPage(current.currentPage);
      var next = this.toSnapshot();
      var isSame = sameCount(current, next) && sameFirstItem(current, next);

      if (!isSame) {
        // Reset to show the first page
        this.paginator.goToPage(1);
      }

      this.announceChanges();
    }
    /**
     * Emit a snapshot view model of the current DataPaginator
     * state...
     */

  }, {
    key: "announceChanges",
    value: function announceChanges() {
      var api = this.toSnapshot();
      this.emitter.next(api);
    }
  }, {
    key: "toSnapshot",
    value: function toSnapshot() {
      var _this$paginator = this.paginator,
          totalCount = _this$paginator.totalCount,
          totalPages = _this$paginator.totalPages,
          currentPage = _this$paginator.currentPage,
          paginatedList = _this$paginator.paginatedList,
          pageSize = _this$paginator.pageSize;
      return {
        totalCount: totalCount,
        totalPages: totalPages,
        currentPage: currentPage,
        paginatedList: paginatedList,
        pageSize: pageSize,
        goToPage: this.goToPage.bind(this),
        setPageSize: this.setPageSize.bind(this)
      };
    }
  }]);

  return RxPaginator;
}();

function sameFirstItem(a, b) {
  var sameNumItems = a.paginatedList.length === b.paginatedList.length;

  var firstID = function firstID(target) {
    return target.paginatedList.length ? target.paginatedList[0].id : null;
  };

  return sameNumItems && firstID(a) === firstID(b);
}

function sameCount(a, b) {
  return a.totalCount === b.totalCount && a.totalPages === b.totalPages;
}

var __DEV__ = true;

function isDev() {
  return __DEV__;
}

var useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

var identity = function identity(s) {
  return s;
};

var NOOP = function NOOP() {};
/**
 * Create a store with specified state
 *
 * The store instances is actually a React hook with Store API added
 * All stores are decorated with Status state (isLoading, error, setIsLoading, and setError)
 *
 * @returns useStore: Hook that will emit either all state or partial state [based on passed selector].
 */


function createStore(createState) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  /**
   * During store startup configuration, we may need to capture
   * initial state and notify store to allow side affects.
   *
   * NOTE: this initialization is for the STORE not for the hook usage...
   *       !!store.reset() does NOT affect the sideaffects from initialization
   */
  var initializer = {
    state: null,
    completed: false,
    registerOnInit: function registerOnInit(notifyMe) {
      initializer.onInit = function () {
        initializer.completed = true;
        var unsubscribe = notifyMe();
        initializer.onDestroy = !!unsubscribe && typeof unsubscribe === 'function' ? unsubscribe : NOOP;
      };
    },
    onInit: NOOP,
    onDestroy: NOOP // call to store is destroyed and cleanup from onReady() activity

  };
  var paginator = new RxPaginator();
  var computed = {};
  var name = options.storeName || "ReactAkitStore".concat(Math.random());
  var resettable = options.hasOwnProperty('autoReset') ? !!options.autoReset : false;
  var recompute = new BehaviorSubject({});
  /**
   * Internal Akita Store + Query instances
   * Note: Immer immutability is auto-applied via the `producerFn` configuration
   */

  var store = new Store({}, {
    producerFn: produce,
    name: name,
    resettable: resettable
  });
  var query = new Query(store);
  /**
   * setIsLoading() + setError()
   * Status API methods available on 'api'
   */

  var setIsLoading = function setIsLoading() {
    var isLoading = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    return store.update(function (s) {
      return Object.assign(Object.assign({}, s), {
        isLoading: isLoading
      });
    });
  };

  var setError = function setError(error) {
    store.update(function (s) {
      return Object.assign(Object.assign({}, s), {
        error: error
      });
    });
  };
  /**
   * setState() + getState()
   * Build API methods used that delegate to the store and query
   */


  var getState = store.getValue.bind(store);

  var setState = function setState(partial) {
    var replace = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var isCallback = partial instanceof Function;

    var updateWithValue = function updateWithValue(s) {
      return replace ? s : Object.assign(Object.assign({}, s), partial);
    };

    store.update(!isCallback ? updateWithValue : partial);
  };

  var applyTransaction$1 = function applyTransaction$1(action, options) {
    var msg = function msg(phase) {
      return "---- ".concat(phase, " applyTransaction() ----- ");
    };

    var log = function log(phase) {
      return (options === null || options === void 0 ? void 0 : options.enableLog) && isDev() && console.log(msg(phase));
    };

    log('start');
    var response = applyTransaction(action, options === null || options === void 0 ? void 0 : options.thisArg);
    log('stop');
    return response;
  };
  /**
   * subscribe()
   * The subscribe function allows components to bind to a state-portion without forcing re-render on changes
   */


  var subscribe = function subscribe(listener, selector) {
    var onNext = function onNext(s) {
      return listener(s);
    };

    var watcher = query.select(selector || identity).subscribe(onNext);
    return function () {
      return watcher.unsubscribe();
    };
  };
  /**
   * addComputedProperty()
   *
   * Inject the computed property into the target 'state':
   *
   * - Subscribe to state changes with the computed property selectors
   * - Use the computed property predicate function to map to a 'value' response
   * - update the targeted computed property with the 'value' response
   *
   * This method is used inside createStore() to configure watches during state
   * initialization.
   *
   * NOTE: At this point, the store/query are not yet ready!! we 'queue' these requests
   *       and later in `registerComputedProperties()` we finish the setup
   *       for all computed properties
   */


  var addComputedProperty = function addComputedProperty(store, property) {
    var list = normalizeProperties(property);
    list.map(function (it) {
      var deferredSetup = function deferredSetup() {
        if (validateComputedProperty(store, it)) {
          var makeQuery = function makeQuery(predicate) {
            return query.select(predicate);
          };

          var selectors = normalizeSelector(it.selectors);
          var emitters = selectors.map(makeQuery);
          var sources$ = emitters.length > 1 ? combineLatest([].concat(_toConsumableArray(emitters), [recompute.asObservable()])) : emitters[0];
          var computed$ = sources$.pipe(map(it.transform), debounceTime(2));

          var onComputedChanged = function onComputedChanged(computedValue) {
            setState(function (s) {
              return Object.assign(Object.assign({}, s), _defineProperty({}, it.name, computedValue));
            });
          };

          if (it.hasOwnProperty('initialValue')) {
            var value = valueFrom(it.initialValue);
            setState(function (s) {
              return Object.assign(Object.assign({}, s), _defineProperty({}, it.name, value));
            });
          }

          var subscription = computed$.subscribe(onComputedChanged);
          return function () {
            return subscription.unsubscribe();
          };
        }

        throw new Error("[createStore::addComputedProperty( '".concat(it.name, "' )] Invalid Store Selectors"));
      };

      computed[it.name] = !initializer.completed ? deferredSetup : deferredSetup();
    });
    return store;
  };
  /**
   * Watch for changes in a specific property and then notify 'listener'
   * This method is used inside createStore() to configure watches during state
   * initialization.
   *
   * NOTE: Unlike observe() which does NOT trigger component rerenders, the 'listener'
   *       has access to `set`; which (if used) will trigger rerenders
   */


  var watchProperty = function watchProperty(store, property, listener) {
    var deferredSetup = function deferredSetup() {
      if (validateWatchedProperty(store, property)) {
        var selector = function selector(s) {
          return s[property];
        };

        var source$ = query.select(selector).pipe(debounceTime(3), distinctUntilChanged());
        listener(selector(store)); // Notify listener immediately

        var watcher = source$.subscribe(listener);
        return function () {
          return watcher.unsubscribe();
        };
      }
    };

    computed[property] = !initializer.completed ? deferredSetup : deferredSetup();
    return store;
  };
  /**
   * Activate all 'queued' computed/watched properties
   */


  var registerComputedProperties = function registerComputedProperties() {
    Object.keys(computed).map(function (propertyName) {
      var registerNow = computed[propertyName];
      computed[propertyName] = registerNow();
    });
  };
  /**
   * Complete streams and close the store
   */


  var destroy = function destroy() {
    Object.keys(computed).map(function (propertyName) {
      var unsubscribe = computed[propertyName];
      unsubscribe();
    });
    store.destroy(); // release internal state and streams

    initializer.onDestroy(); // enable store cleanup of custom side affects
  };
  /**
   * Do not auto-destroy the store on hook/component dismount
   * stores can be shared and persistent between mountings.
   *
   * Reset() for two conditions:
   *
   * (1) Dismount - When component unmounts and the hook is released,
   *                ^ this requires options 'resettable' to be true
   * (2) Imperative - When shared state should be cleared/reset
   *                  ^ available anytime so the shared hook can reset state
   *
   * Reset store to initial state and force recompute of properties
   *
   */


  var _reset = function reset() {
    var forced = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    // we need our computed values to recompute.
    resettable && store.reset();
    recompute.next(Object.assign({}, initializer.state));

    if (forced) {
      var nextState = produce({}, function () {
        return Object.assign({}, initializer.state);
      });

      store._setState(nextState);
    }
  }; // unsubscribe from paginator.pagination$ stream


  var unsubscribe = NOOP;
  /**
   * Optional Paginator API available within the createStore factory
   *
   * The target 'rawlist' is unaffected and passed thru...Meanwhile,
   * the paginated list internally manages a clone of original target 'rawlist'
   */

  var paginate = function paginate(rawList) {
    var pageSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 20;

    var watchPaginator = function watchPaginator() {
      var stream$ = paginator.pagination$;
      var subscription = stream$.subscribe(function (pagination) {
        setState(function (s) {
          s.pagination = pagination;
        });
      });
      unsubscribe = subscription.unsubscribe.bind(subscription);
    };

    paginator = new RxPaginator(rawList, pageSize);
    unsubscribe();
    watchPaginator();
    return rawList;
  };
  /**
   * Decorate the paginate function with a tail-hook;
   * to allow the `paginate()` feature to compose 'on' another function
   * e.g
   *    addComputedProperty(store, {
   *      name: 'evenKeys',
   *      selectors: (s) => s.allKeys,
   *      transform: paginate.on(computeEvenKeys)
   *    });
   *
   */


  paginate.on = function (fn) {
    var pageSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 20;
    return function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      var results = fn.apply(null, args); // Start a side affect to update pagination OUTSIDE computed property
      // transforms.

      new Promise(function (resolve) {
        paginate(results, pageSize);
      });
      return results;
    };
  };
  /**
   * Create the Store instance with desired API
   */


  var storeAPI = {
    get: getState,
    set: setState,
    applyTransaction: applyTransaction$1,
    addComputedProperty: addComputedProperty,
    watchProperty: watchProperty,
    setIsLoading: setIsLoading,
    setError: setError,
    paginate: paginate // easily set pagination state and API for target dataset

  };
  var hookAPI = {
    observe: subscribe,
    destroy: destroy,
    reset: function reset() {
      return _reset(true);
    } // allow hook-level resets of store information; without destroying streams

  };
  /**
   * Internal utility methods for selectors
   *
   * toObservable(): Build an RxJS stream for the specified selector(s)
   * toStateSlice(): Gather current state values for the specified selector(s)
   */

  var toObservable = function toObservable(selector) {
    var list = normalizeSelector(selector);

    var buildQueries = function buildQueries() {
      return list.map(function (it) {
        return query.select(it);
      });
    };

    var buildStream = function buildStream() {
      return list.length ? combineLatest(buildQueries()) : query.select(identity);
    };

    return buildStream().pipe(debounceTime(10));
  };

  var getSliceValueFor = function getSliceValueFor() {
    var selector = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : identity;
    var list = selector instanceof Array ? selector : null;

    var getCurrent = function getCurrent(it) {
      return it(getState());
    };

    return list ? list.map(getCurrent) : getCurrent(selector);
  };
  /**
   * userStore()
   *
   * After the store has been created and initialized with api + state, this hoook
   * is created. Developer will use this custom react hook to 'select' state data.
   * The selected data sources will re-emit data after changes.
   *
   * This hook is implicitly connected to its associated [parent] store
   *
   * Warning!!
   *   1) Do not use same 'useStore' hook multiple times in the same component.
   *   2) To extract multiple slices, use
   *      a) a single selector to return a reponse (value, hashmap, or tuple)
   *      b) an array of selectors [optimized approach] to return response
   */


  var useStore = function useStore(selector) {
    var _useState = useState(function () {
      return getSliceValueFor(selector);
    }),
        _useState2 = _slicedToArray(_useState, 1),
        initial = _useState2[0];

    var _useObservable = useObservable(null, initial),
        _useObservable2 = _slicedToArray(_useObservable, 2),
        slice = _useObservable2[0],
        setSlice$ = _useObservable2[1];

    useIsoLayoutEffect(function () {
      setSlice$(toObservable(selector));
      return function () {
        _reset();
      };
    }, [selector]);
    return slice;
  };
  /**
   * Initialization of state management
   * Create the immutable state using the 'createState()' custom store
   * factory function
   *
   * - Reinitialize the internal store,
   * - Register computed and watched properties
   * - inject storeAPI
   */


  var initializeStore = function initializeStore() {
    initializer.state = produce({}, function () {
      return Object.assign(Object.assign({
        error: null,
        isLoading: false
      }, {
        pagination: {
          paginatedList: [],
          totalCount: 0,
          totalPages: 1,
          currentPage: 1,
          pageSize: 20
        }
      }), createState(Object.assign(Object.assign({}, storeAPI), hookAPI), // provide to custom store internal functions
      initializer.registerOnInit // enable custom store to register for useStoreEffect notifications
      ));
    });
    reinitStore(store, name, initializer.state);
    registerComputedProperties(); // Notify store initialization is done and side affects are now allowed

    initializer.onInit(); // Decorate hook function with API methods, public custom hook/store

    return Object.assign(useStore, hookAPI);
  };

  return initializeStore();
} // *******************************************************
// Hacks
// *******************************************************

/**
 * Currently Akita expects a store class to be decorated with @StoreConfi() to set a store name
 * If this is not done, the constructor will assert a `console.error()`
 * @param store
 * @param name
 */

function reinitStore(store, name, state) {
  if (!!name && store.storeName != name) {
    store.constructor['akitaConfig'].storeName = name;
    store['options'].storeName = name;
  } // Reinitalize


  store['onInit'](state);
}
/**
 * Ensure that a 'list' of selectors is available for upcoming iteration
 */


function normalizeSelector(selectors) {
  var isArray = selectors instanceof Array;
  return isArray ? selectors : !!selectors ? [selectors] : [];
}
/**
 * Ensure that the 'list' of computedProperties is available for upcoming iteration
 */


function normalizeProperties(list) {
  var isArray = list instanceof Array;
  return isArray ? list : [list];
}
/**
 * Computed property validation
 *  - is the property defined in the state (eg should have a placeholder starting value)
 *  - are you using 2 or more state selectors
 */


function validateComputedProperty(store, property) {
  var isValidProperty = validateWatchedProperty(store, property.name, 'ComputedProperty');
  var validSelectors = validateSelectors(store, property.selectors);

  if (!validateSelectors) {
    console.error("\n      ComputedProperty '".concat(property.name, "' cannot select the store. \n      Please specify 1 or more properites within the store.    \n    "));
  }

  return isValidProperty && validSelectors;
}
/**
 * Computed property validation
 *  - is the property defined in the state (eg should have a placeholder starting value)
 *  - are you using 2 or more state selectors
 */


function validateWatchedProperty(store, name) {
  var fieldType = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'WatchProperty';
  var hasProperty = store.hasOwnProperty(name);

  if (!hasProperty && isDev()) {
    console.warn("\n      ".concat(fieldType, " '").concat(name, "' may not be a valid property in your store. \n    "));
  }

  return hasProperty;
}
/**
 * No selector is allowed to select entire store/state
 * @param store
 * @param current List of state selectors OR a single selector
 * @returns boolean All selectors are valid
 */


function validateSelectors(store, current) {
  var selectors = normalizeSelector(current);

  var test = function test(selector) {
    return selector(store) != store;
  };

  return selectors.reduce(function (valid, sel) {
    return valid && test(sel);
  }, true);
}
/**
 * Convert input source to output value
 * Input source may be a factory OR the value itself.
 */


function valueFrom(source) {
  return typeof source === 'function' ? source() : source;
}

/**
 *  Utility `switchCase` to functionaly make case statements easy to process by
 *  using a case hashmap:
 *
 *  @Code
 *    (1) Used to perform quick lookups with fallbacks
 *
 *        const dayofWeek: SwitchCase<number, string> = switchCase({
 *          0: 'Sunday',
 *          1: 'Monday',
 *          2: 'Tuesday',
 *          3: 'Wednesday',
 *          4: 'Thursday',
 *          5: 'Friday',
 *          6: 'Saturday'
 *        },'Unknown'));
 *
 *        const currentDay = dayofWeek(new Date().getDay())
 *
 *
 *     (2)  Used to simply Redux state reducers:
 *
 *         export const counterReducer = <T extends ActionNames<typeof handleAction>>(
 *           state: number, action: { type: T }
 *         ) => {
 *           const handleAction = switchCase({
 *              'INCREMENT': () => state + 1,
 *              'DECREMENT': () => state - 1,
 *              'RESET': 0,
 *           }, state);
 *           return handleAction(action.type);
 *         };
 *
 *         console.log(counterReducer(0, { type: 'INCREMENT' }));      // perfect
 *         console.log(counterReducer("wrong", { type: 'FOO' }));      // intellisense shows errors
 *         console.log(counterReducer(0, { type: 'FOO' }));            // intellisense shows errors
 */
var findCaseValue = function findCaseValue(cases, defaultCase) {
  return function (key) {
    return cases[key] || defaultCase;
  };
};

var switchCase = function switchCase(cases, defaultCase) {
  return function (key) {
    var f = findCaseValue(cases, defaultCase)(key);
    return f instanceof Function ? f() : f;
  };
};

export { CollectionEvent, DataPaginator, DependencyInjectionProvider, EventBus, InjectionToken, InjectorContext, ItemEvent, PAGE_SIZE, RxPaginator, Type, concatLatestFrom, createStore, destroyEventBus, isType, itemError, itemRemoved, itemUpdated, makeInjector, readAll, readFirst, rootInjector, switchCase, useDependencyInjector, useEventBus, useInjectorHook, useObservable, useStorePaginator };
